import type { ArtifactStatus } from "@elation/api-client/src/stitch-web";

/**
 * Server lifecycle for generated encounter artifacts that expose a status
 * field (codes via `codingStatus`, agenda via derived `artifactStatus`).
 * Action items use list query lifecycle only — no durable generation status.
 */
export type GeneratedArtifactStatus = ArtifactStatus;

/**
 * UI-facing view state derived from status, row count, and request lifecycle.
 * Distinguishes fetch errors from generation failures for panel copy.
 */
export type GeneratedArtifactViewState =
  | "generating"
  | "not-started"
  | "ready"
  | "empty"
  | "fetch-error"
  | "generation-failed";

export const GENERATED_ARTIFACT_POLL_INTERVAL_MS = 3_000 as const;

/**
 * Derives the panel display state. Already-arrived rows stay on `ready` during
 * refetch and while generation is still pending.
 *
 * Settled empty is strictly `ready` + zero rows. `none` means generation has
 * not started yet — a distinct `not-started` state (not a spinner, not empty).
 */
export function resolveGeneratedArtifactViewState(input: {
  artifactStatus?: GeneratedArtifactStatus | null;
  itemCount: number;
  isInitialLoading: boolean;
  isFetchError: boolean;
}): GeneratedArtifactViewState {
  const { artifactStatus, itemCount, isInitialLoading, isFetchError } = input;

  if (isFetchError && itemCount === 0) {
    return "fetch-error";
  }

  if (itemCount > 0) {
    return "ready";
  }

  if (artifactStatus === "failed") {
    return "generation-failed";
  }

  if (isInitialLoading || artifactStatus === "pending") {
    return "generating";
  }

  if (artifactStatus === "none") {
    return "not-started";
  }

  if (artifactStatus === "ready") {
    return "empty";
  }

  // Missing status after a successful empty fetch (e.g. transcript) → empty.
  return "empty";
}

/**
 * True when rows are visible but the server says generation may still produce more.
 */
export function isGeneratedArtifactStillPending(input: {
  artifactStatus?: GeneratedArtifactStatus | null;
  itemCount: number;
}): boolean {
  return input.artifactStatus === "pending" && input.itemCount > 0;
}

/**
 * Poll while generation may still start (`none`) or is in flight (`pending`).
 * Terminal `ready`/`failed` stop polling.
 */
export function generatedArtifactPollIntervalMs(
  artifactStatus: GeneratedArtifactStatus | null | undefined,
  intervalMs: typeof GENERATED_ARTIFACT_POLL_INTERVAL_MS = GENERATED_ARTIFACT_POLL_INTERVAL_MS,
): typeof GENERATED_ARTIFACT_POLL_INTERVAL_MS | false {
  return artifactStatus === "pending" || artifactStatus === "none"
    ? intervalMs
    : false;
}
